import { jsPDF } from 'jspdf';
import type { NumerosDoBalanco } from '@shared/balanco.js';

/**
 * O balanço do ciclo em PDF (etapa 16), gerado no navegador como o
 * certificado e o relatório 5 Forças. Branco, preto e o amarelo da Hack.
 * Sem a recomendação à gestão: o PDF é dos sócios.
 */

type Numeros = Omit<NumerosDoBalanco, 'periodo' | 'indicadores'> & {
  periodo: { inicio: string; fim: string | null; mes: number };
  indicadores: (Omit<NumerosDoBalanco['indicadores'][number], 'primeiro' | 'ultimo'> & {
    primeiro: { competencia: string; valor: number };
    ultimo: { competencia: string; valor: number };
  })[];
};

const AM: [number, number, number] = [255, 200, 0];
const PRETO: [number, number, number] = [12, 12, 14];
const CINZA: [number, number, number] = [110, 110, 115];

const data = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC' }) : '—');
const mes = (iso: string) => new Date(iso).toLocaleDateString('pt-BR', { month: 'short', year: 'numeric', timeZone: 'UTC' });
const pl = (n: number, um: string, varios: string) => `${n} ${n === 1 ? um : varios}`;
const n1 = (x: number | null) => (x == null ? '—' : x.toLocaleString('pt-BR', { maximumFractionDigits: 1 }));
const valor = (x: number | null, u: string | null) =>
  x == null ? '—' : `${x.toLocaleString('pt-BR', { maximumFractionDigits: 2 })}${u ? ` ${u}` : ''}`;

export function baixarBalancoPdf(d: { empresa: string; publicado_em: string | null; parecer: string | null; numeros: Numeros }): void {
  const doc = new jsPDF({ unit: 'mm', format: 'a4' });
  const W = 210;
  const M = 18;
  const L = W - M * 2;
  let y = 0;

  const novaPagina = () => {
    doc.addPage();
    y = M;
  };
  const precisa = (h: number) => {
    if (y + h > 297 - M) novaPagina();
  };
  const titulo = (t: string) => {
    precisa(14);
    y += 4;
    doc.setFillColor(AM[0], AM[1], AM[2]);
    doc.rect(M, y - 4, 2, 6, 'F');
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(12);
    doc.setTextColor(PRETO[0], PRETO[1], PRETO[2]);
    doc.text(t, M + 5, y);
    y += 7;
  };
  const texto = (t: string, o: { tam?: number; cor?: [number, number, number]; negrito?: boolean; recuo?: number } = {}) => {
    doc.setFont('helvetica', o.negrito ? 'bold' : 'normal');
    doc.setFontSize(o.tam ?? 10);
    const c = o.cor ?? PRETO;
    doc.setTextColor(c[0], c[1], c[2]);
    const linhas = doc.splitTextToSize(t, L - (o.recuo ?? 0)) as string[];
    for (const l of linhas) {
      precisa(5.2);
      doc.text(l, M + (o.recuo ?? 0), y);
      y += 5.2;
    }
  };

  // Capa: a faixa preta com o nome.
  doc.setFillColor(PRETO[0], PRETO[1], PRETO[2]);
  doc.rect(0, 0, W, 46, 'F');
  doc.setFillColor(AM[0], AM[1], AM[2]);
  doc.rect(0, 46, W, 1.5, 'F');
  doc.setTextColor(AM[0], AM[1], AM[2]);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(10);
  doc.text('HACK ACADEMY · CONSELHEIRO', M, 16);
  doc.setTextColor(255, 255, 255);
  doc.setFontSize(20);
  doc.text('Balanço do ciclo', M, 28);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(11);
  doc.text(
    `${d.empresa} · ${data(d.numeros.periodo.inicio)} a ${data(d.numeros.periodo.fim)}${d.publicado_em ? ` · publicado em ${data(d.publicado_em)}` : ''}`,
    M,
    38,
  );
  y = 58;

  if (d.parecer) {
    titulo('O parecer do conselheiro');
    for (const p of d.parecer.split(/\n{2,}/)) {
      texto(p.trim());
      y += 2;
    }
  }

  const r = d.numeros.radar;
  titulo('O radar: de onde partiu, onde chegou');
  texto(
    `Média de ${n1(r.partida)} na partida e ${n1(r.final)} ${r.final_tipo === 'MARCO' ? 'no último marco' : 'no radar final'}.` +
      (r.mais_avancaram.length ? ` Avançaram mais: ${r.mais_avancaram.join(', ')}.` : '') +
      (r.recuaram.length ? ` Recuaram: ${r.recuaram.join(', ')}.` : ''),
    { cor: CINZA },
  );
  y += 1;
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(CINZA[0], CINZA[1], CINZA[2]);
  precisa(6);
  doc.text('Tema', M, y);
  doc.text('Partida', M + 90, y, { align: 'right' });
  doc.text('Final', M + 115, y, { align: 'right' });
  doc.text('Variação', M + 145, y, { align: 'right' });
  y += 2;
  doc.setDrawColor(220, 220, 222);
  doc.line(M, y, M + L, y);
  y += 4;
  doc.setFont('helvetica', 'normal');
  for (const t of r.temas) {
    precisa(6);
    doc.setTextColor(PRETO[0], PRETO[1], PRETO[2]);
    doc.text(t.nome, M, y);
    doc.text(n1(t.partida), M + 90, y, { align: 'right' });
    doc.text(n1(t.final), M + 115, y, { align: 'right' });
    if (t.delta != null) {
      doc.setFont('helvetica', 'bold');
      doc.text(`${t.delta > 0 ? '+' : ''}${n1(t.delta)}`, M + 145, y, { align: 'right' });
      doc.setFont('helvetica', 'normal');
    }
    y += 5.5;
  }

  if (d.numeros.metas.length) {
    titulo('As Metas do ciclo');
    for (const m of d.numeros.metas) {
      texto(`${m.nome}: ${m.descricao}`, { negrito: true });
      const pct = m.progresso == null ? null : Math.round(m.progresso * 100);
      texto(
        `De ${valor(m.linha_base, m.unidade)} para ${valor(m.alvo, m.unidade)}; chegou a ${valor(m.atual, m.unidade)}` +
          (pct == null ? '.' : ` — ${pct}% do caminho${m.atingida ? ', meta atingida' : ''}.`),
        { cor: CINZA },
      );
      if (pct != null) {
        precisa(5);
        doc.setFillColor(235, 235, 237);
        doc.rect(M, y - 2.5, 80, 2.5, 'F');
        doc.setFillColor(AM[0], AM[1], AM[2]);
        doc.rect(M, y - 2.5, (80 * pct) / 100, 2.5, 'F');
        y += 3;
      }
      y += 2;
    }
  }

  const dc = d.numeros.decisoes;
  titulo('As decisões');
  texto(
    `${pl(dc.total, 'decisão', 'decisões')} no ciclo: ${pl(dc.concluidas, 'concluída', 'concluídas')} (${dc.no_prazo} no prazo), ${dc.em_aberto} em aberto` +
      `${dc.atrasadas ? `, ${pl(dc.atrasadas, 'atrasada', 'atrasadas')}` : ''}${dc.canceladas ? ` e ${pl(dc.canceladas, 'cancelada', 'canceladas')}` : ''}.` +
      (dc.cumprimento != null ? ` Cumprimento: ${dc.cumprimento}%.` : ''),
  );

  if (d.numeros.indicadores.length) {
    titulo('Os indicadores');
    for (const i of d.numeros.indicadores) {
      texto(
        `${i.nome}: ${valor(i.primeiro.valor, i.unidade)} em ${mes(i.primeiro.competencia)}, ${valor(i.ultimo.valor, i.unidade)} em ${mes(i.ultimo.competencia)}` +
          (i.dentro_da_faixa ? '' : ' (fora da faixa esperada)'),
      );
    }
  }

  const mh = d.numeros.manhas;
  titulo('As manhãs');
  texto(
    `${pl(mh.realizadas, 'manhã realizada', 'manhãs realizadas')}${mh.canceladas ? ` e ${pl(mh.canceladas, 'cancelada', 'canceladas')}` : ''}.` +
      (mh.nota_media != null ? ` Nota média dos sócios: ${n1(mh.nota_media)} de 5 (${mh.avaliacoes} avaliações).` : ''),
  );

  const paginas = doc.getNumberOfPages();
  for (let p = 1; p <= paginas; p++) {
    doc.setPage(p);
    doc.setFontSize(8);
    doc.setTextColor(CINZA[0], CINZA[1], CINZA[2]);
    doc.text(`Confidencial · ${d.empresa} · página ${p} de ${paginas}`, W / 2, 297 - 8, { align: 'center' });
  }
  const nome = d.empresa.normalize('NFD').replace(/[^\w\s-]/g, '').trim().replace(/\s+/g, '-').toLowerCase();
  doc.save(`balanco-do-ciclo-${nome || 'empresa'}.pdf`);
}
